import { useState, type FormEvent } from 'react';
import { ShopError, api, type Review } from '../api';
import { Stars, StarPicker } from './Stars';

/**
 * Recenze na detailu produktu.
 *
 * Souhrn nahoře, pod ním seznam schválených recenzí a formulář. Nová recenze
 * se NEUKÁŽE hned – čeká na schválení obsluhou, a formulář to po odeslání
 * řekne. Slíbit okamžité zveřejnění by znamenalo lhát o tom, jak to funguje.
 */
export function ProductReviews({
  slug,
  average,
  count,
  reviews,
}: {
  slug: string;
  average: number;
  count: number;
  reviews: readonly Review[];
}) {
  const [rating, setRating] = useState(0);
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (sending) return;
    if (rating < 1) {
      setError('Vyber prosím počet hvězd.');
      return;
    }

    setSending(true);
    setError(null);
    try {
      await api.submitReview(slug, {
        rating,
        ...(name.trim() === '' ? {} : { authorName: name.trim() }),
        ...(body.trim() === '' ? {} : { body: body.trim() }),
      });
      setDone(true);
    } catch (caught) {
      setError(caught instanceof ShopError ? caught.message : 'Odeslání se nezdařilo.');
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="reviews">
      <div className="reviews-head">
        <h2>Hodnocení zákazníků</h2>
        {count > 0 ? (
          <Stars value={average} count={count} size="lg" />
        ) : (
          <span className="muted">Zatím bez hodnocení. Buďte první.</span>
        )}
      </div>

      {reviews.length > 0 && (
        <ul className="review-list">
          {reviews.map((review) => (
            <li key={review.id} className="review">
              <div className="review-top">
                <Stars value={review.rating} size="sm" />
                <strong>{review.authorName === '' ? 'Anonym' : review.authorName}</strong>
                <span className="muted small">{formatDate(review.createdAt)}</span>
              </div>
              {review.body !== '' && <p className="review-body">{review.body}</p>}
            </li>
          ))}
        </ul>
      )}

      {done ? (
        <p className="notice">
          Děkujeme za hodnocení! Zveřejníme ho, jakmile ho projde naše obsluha.
        </p>
      ) : (
        <form className="review-form card" onSubmit={submit}>
          <h3>Napište hodnocení</h3>

          <div className="field">
            <span>Vaše hvězdy</span>
            <StarPicker value={rating} onChange={setRating} />
          </div>

          <label className="field">
            <span>Jméno (nepovinné)</span>
            <input
              type="text"
              maxLength={80}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Jak se podepíšete"
            />
          </label>

          <label className="field">
            <span>Co na produktu oceňujete? (nepovinné)</span>
            <textarea
              rows={3}
              maxLength={2000}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="Zkušenost s produktem…"
            />
          </label>

          {error !== null && <p className="field-error">{error}</p>}

          <button type="submit" className="button primary" disabled={sending}>
            {sending ? 'Odesílám…' : 'Odeslat hodnocení'}
          </button>
        </form>
      )}
    </section>
  );
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
}
